import { Validator } from 'fluentvalidation-ts';
import produce from 'immer';
import { includes, map, some, uniqueId } from 'lodash';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { fieldRequiredValidationMessage } from '../../../../validators/sharedValidators';
import {
  beAnInteger,
  beEqualTo,
  beGreaterThanOrEqualTo,
  beLessThanOrEqualTo,
  beNumeric,
  bePositiveNumber,
  haveDecimalPlaces,
} from '../../../../validators/stringValidators';
import { ManifoldFloorConstructionFormModel, PumpFormModel } from '../GetDataForQuoteOrDesignForm';
import {
  allowedThermostatTypeCodesForElectricUnderfloorHeating,
  FloorLevelCode,
  ThermostatTypeCode,
  thermostatTypeCodesForElectricUnderfloorHeatingOnly,
} from '../quoteOrDesignFormData';
import { createDuplicateRoom } from '../rooms/RoomFields';
import { initialRoomFormModel, RoomFormModel, RoomFormModelValidator } from '../rooms/roomFormModel';
import { UnitFormModel } from '../units/unitFormModel';
import {
  DesignManifoldDetailsFormModel,
  DesignManifoldDetailsFormModelValidator,
} from './designManifoldDetailsFormModel';

export type ManifoldFormModel = {
  manifoldId: number | null;
  id: string;
  isExpanded: boolean;
  name: string;
  isRadiator: boolean;
  roomlessManifold: RoomlessManifoldFormModel | null;
  rooms: Array<RoomFormModel> | null;
  totalUnderfloorHeatingAreaInMetresSquared: string;
  totalNumberOfPorts: string;
  skirtingHeatingValidation: null;
  pump: PumpFormModel | null;
  designManifoldDetails: DesignManifoldDetailsFormModel | null;
};

export const initialManifoldId = 'initial-manifold';

export const initialManifoldFormModel: ManifoldFormModel = {
  manifoldId: null,
  id: initialManifoldId,
  isExpanded: true,
  name: 'Manifold A',
  isRadiator: false,
  rooms: [initialRoomFormModel],
  totalUnderfloorHeatingAreaInMetresSquared: '',
  totalNumberOfPorts: '',
  skirtingHeatingValidation: null,
  roomlessManifold: null,
  pump: null,
  designManifoldDetails: null,
};

export const createDuplicateManifold = (manifold: ManifoldFormModel) => {
  return produce(manifold, draftManifold => {
    draftManifold.manifoldId = null;
    draftManifold.id = uniqueId('manifold');
    draftManifold.rooms =
      draftManifold.roomlessManifold != null ? null : map(draftManifold.rooms, room => createDuplicateRoom(room));
  });
};

export class ManifoldFormModelValidator extends Validator<ManifoldFormModel> {
  constructor(isDraft: boolean, unit: UnitFormModel) {
    super();

    this.ruleFor('name')
      .notEmpty()
      .withMessage('Please enter a name for this Manifold')
      .maxLength(100)
      .withMessage('Please enter a name that is no more than 100 characters long');

    if (!isDraft) {
      this.ruleFor('totalNumberOfPorts')
        .must(beLessThanOrEqualTo(12))
        .withMessage('A single Manifold can have at most 12 ports');

      this.ruleFor('pump')
        .notNull()
        .withMessage(fieldRequiredValidationMessage)
        .when(
          manifold =>
            !unit.hasPipelineMap &&
            unit.pumpLocation === 'PumpOnManifold' &&
            unit.pumpSelectionType !== 'PumpsNotSupplied' &&
            !manifoldHasSkirtingHeating(manifold),
        );

      this.ruleFor('skirtingHeatingValidation')
        .must((_, { rooms }) => {
          const skirtingHeatingRooms = assertNotNull(rooms).filter(
            room => room.floorConstruction != null && room.floorConstruction.isSkirtingHeating,
          );
          const nonSkirtingHeatingRooms = assertNotNull(rooms).filter(
            room => room.floorConstruction != null && !room.floorConstruction.isSkirtingHeating,
          );
          return skirtingHeatingRooms.length === 0 || nonSkirtingHeatingRooms.length === 0;
        })
        .withMessage('A manifold cannot serve both UFH rooms and skirting heating rooms')
        .when(manifold => manifold.rooms != null);

      this.ruleFor('skirtingHeatingValidation')
        .must((_, { rooms }) => {
          const skirtingHeatingRooms = assertNotNull(rooms).filter(
            room => room.floorConstruction != null && room.floorConstruction.isSkirtingHeating,
          );
          const distinctSkirtingHeatingTypes = [
            ...Array.from(new Set(skirtingHeatingRooms.map(room => assertNotNull(room.floorConstruction).partId))),
          ];

          return distinctSkirtingHeatingTypes.length <= 1;
        })
        .withMessage('A manifold cannot have a mix of different skirting heating floor constructions')
        .when(manifold => manifold.rooms != null);
    }

    this.ruleFor('roomlessManifold').setValidator(() => new RoomlessManifoldFormModelValidator(isDraft));

    this.ruleForEach('rooms').setValidator(manifold => new RoomFormModelValidator(manifold.isRadiator, isDraft));

    this.ruleFor('designManifoldDetails').setValidator(() => new DesignManifoldDetailsFormModelValidator(isDraft));
  }
}

export type RoomlessManifoldFormModel = {
  floorLevelCode: FloorLevelCode | null;
  floorConstruction: ManifoldFloorConstructionFormModel | null;
  areaInMetresSquared: string;
  numberOfPorts: string;
  thermostatTypeCode: ThermostatTypeCode;
  numberOfThermostats: string;
  numberOfWetSensors: string;
  numberOfFloorSensors: string;
};

export class RoomlessManifoldFormModelValidator extends Validator<RoomlessManifoldFormModel> {
  constructor(isDraft: boolean) {
    super();

    this.ruleFor('floorLevelCode')
      .notNull()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('floorConstruction')
      .notNull()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('areaInMetresSquared')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1))
      .unless(() => isDraft);

    this.ruleFor('areaInMetresSquared')
      .must(beLessThanOrEqualTo(19))
      .when(
        roomlessManifold =>
          !isDraft &&
          roomlessManifold.floorConstruction != null &&
          roomlessManifold.floorConstruction.isElectricUnderfloorHeating,
      );

    this.ruleFor('numberOfPorts')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(beAnInteger)
      .must(bePositiveNumber)
      .must(beLessThanOrEqualTo(12))
      .withMessage('A single Manifold can have at most 12 ports')
      .unless(
        roomlessManifold =>
          isDraft ||
          (roomlessManifold.floorConstruction != null &&
            roomlessManifold.floorConstruction.isElectricUnderfloorHeating),
      );

    this.ruleFor('numberOfThermostats')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft, 'AppliesToCurrentValidator')
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(beAnInteger)
      .must(beLessThanOrEqualTo(1000))
      .unless(() => isDraft);

    this.ruleFor('numberOfThermostats')
      .must(
        (numberOfThermostats, roomlessManifold) =>
          Number(numberOfThermostats) <= Number(roomlessManifold.numberOfPorts),
      )
      .withMessage('Number of thermostats cannot exceed number of ports on a manifold')
      .unless(
        roomlessManifold =>
          isDraft ||
          (roomlessManifold.floorConstruction != null &&
            roomlessManifold.floorConstruction.isElectricUnderfloorHeating),
      );

    this.ruleFor('numberOfThermostats')
      .equal('0')
      .must(beEqualTo(0))
      .withMessage('Number of thermostats must be 0 when thermostat type is "Shared"')
      .when(room => !isDraft && room.thermostatTypeCode === 'Shared');

    this.ruleFor('numberOfWetSensors')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(beAnInteger)
      .must(beLessThanOrEqualTo(1000))
      .unless(() => isDraft);

    this.ruleFor('numberOfWetSensors')
      .must(beEqualTo(0))
      .withMessage('Number of wet sensors must be 0 when thermostat type is "Shared"')
      .when(room => !isDraft && room.thermostatTypeCode === 'Shared');

    this.ruleFor('numberOfFloorSensors')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(beGreaterThanOrEqualTo(0))
      .must(beAnInteger)
      .must(beLessThanOrEqualTo(1000))
      .unless(() => isDraft);

    this.ruleFor('numberOfFloorSensors')
      .must(beEqualTo(0))
      .withMessage('Number of floor sensors must be 0 when thermostat type is "Shared"')
      .when(room => !isDraft && room.thermostatTypeCode === 'Shared');

    this.ruleFor('thermostatTypeCode')
      .must(thermostatTypeCode => includes(allowedThermostatTypeCodesForElectricUnderfloorHeating, thermostatTypeCode))
      .withMessage('Cannot select this thermostat type when the floor construction is of type "Electric UFH"')
      .when(room => !isDraft && room.floorConstruction != null && room.floorConstruction.isElectricUnderfloorHeating);

    this.ruleFor('thermostatTypeCode')
      .must(thermostatTypeCode => !includes(thermostatTypeCodesForElectricUnderfloorHeatingOnly, thermostatTypeCode))
      .withMessage('Cannot select this thermostat type when the floor construction is not of type "Electric UFH"')
      .when(room => !isDraft && room.floorConstruction != null && !room.floorConstruction.isElectricUnderfloorHeating);

    this.ruleFor('thermostatTypeCode')
      .must(thermostatTypeCode => thermostatTypeCode !== 'Shared')
      .withMessage('Cannot have a shared thermostat on a roomless manifold');
  }
}

export const manifoldHasSkirtingHeating = (manifold: ManifoldFormModel) =>
  some(manifold.rooms, room => room.floorConstruction && room.floorConstruction.isSkirtingHeating);
